import React, { useContext } from 'react';
import styled from 'styled-components';

import { DashboardFiltersContext } from '../../contextProviders/DashboardFiltersProvider';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import { EXCLUDE_HEIGHT } from '../../constants';
import FilterPlates from '../FilterPlates';
import PinnedFilterPlates from '../Report/PinnedFilters/PinnedFilterPlates';
import usePinnedFilterSlots from '../Report/PinnedFilters/usePinnedFilterSlots';
import QuickFilterPills from '../Report/QuickFilters/QuickFilterPills';
import ResetReportButton from '../Report/ResetReportButton/ResetReportButton';
import useDataTypesOnReportCanvas from '../Report/useDataTypesOnReportCanvas';

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  position: sticky;
  top: 0px;
  z-index: 11;
  background-color: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-secondary);
`;

// What viewers filter by: the quick filters to toggle and the pinned fields
// to pick values for. Must sit inside the bar's FilterPlatesProvider, which
// the pinned fields' values are set through.
const ViewerFilters = ({ dashboard }: { dashboard: DashboardType }) => {
  const dashboardFilters = useContext(DashboardFiltersContext);
  const { slots, hiddenDrillDownIds } = usePinnedFilterSlots({
    pins: dashboard.pinnedFilterFields,
  });
  if (!dashboardFilters) {
    return null;
  }

  const {
    drillDowns,
    quickFilters,
    selectedQuickFilters,
    setSelectedQuickFilters,
    reset,
  } = dashboardFilters;
  const hasSelections =
    drillDowns.length > 0 || selectedQuickFilters.length > 0;

  return (
    <>
      <QuickFilterPills
        quickFilters={quickFilters}
        quickFilterOrder={dashboard.quickFilterOrder || window.emptyArray}
        selectedQuickFilters={selectedQuickFilters}
        setSelectedQuickFilters={setSelectedQuickFilters}
      />
      {/* No "Add filter": viewers filter by what's been set up for them.
          Values left for a field no longer pinned still show, to remove. */}
      <FilterPlates
        isReport
        isEveryFilterInteractive
        hideAddFilterButton
        hiddenDrillDownIds={hiddenDrillDownIds}
        leadingChildren={<PinnedFilterPlates slots={slots} />}
      >
        {hasSelections && <ResetReportButton onClick={reset} />}
      </FilterPlates>
    </>
  );
};

// The dashboard's filter bar, laid out like a report's. Viewers get its quick
// filters and pinned fields, and see no bar when it has neither. In edit mode
// it edits the saved default filters. All of them apply to every dashboard
// gadget.
const DashboardFiltersBar = ({
  dashboard,
  currentCanvas,
  isEditing,
}: {
  dashboard: DashboardType;
  currentCanvas: Canvas;
  isEditing: boolean;
}) => {
  const dashboardFilters = useContext(DashboardFiltersContext);
  const dataTypes = useDataTypesOnReportCanvas(currentCanvas);

  if (!dashboardFilters) {
    return null;
  }

  const { scope, setScope, drillDowns, setDrillDowns, quickFilters } =
    dashboardFilters;
  const hasViewerFilters =
    quickFilters.length > 0 || (dashboard.pinnedFilterFields ?? []).length > 0;
  if (!isEditing && !hasViewerFilters) {
    return null;
  }

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      scope={scope}
      setScope={setScope}
      dataTypes={dataTypes}
    >
      <FiltersRow
        className={`${EXCLUDE_HEIGHT}-${dashboard.id}`}
        data-testid="dashboard-filters"
      >
        {isEditing ? (
          <FilterPlates isScope />
        ) : (
          <ViewerFilters dashboard={dashboard} />
        )}
      </FiltersRow>
    </FilterPlatesProvider>
  );
};

export default DashboardFiltersBar;
